import {
    paramAppend,
    paramDsNameSrc,
    paramPattern,
    paramRelDest,
    paramString1,
    paramString2,
    paramXPathSrc,
} from '../constants/parameterNames';
import {
    typeBoolean,
    typeDataSource,
    typeXPath,
} from '../constants/parameterTypes';
import { failure, success } from '../helpers/commandResult';
import {
    getBoolParamValue,
    getParamValue,
} from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import { getValidNode, getValidNodeSet } from '../helpers/qRules.node.helpers';

const optionalParameters = [
    {
        name: paramDsNameSrc,
        description: `Name of the data source containing the source value indicated by /${paramXPathSrc}`,
        type: typeDataSource,
    },
    {
        name: paramString1,
        description: `Value to be replaced - either /${paramString1} or /${paramPattern} must be provided`,
    },
    {
        name: paramString2,
        description: `Value to replace with. If not specified, the found value is removed from the string.\n\nWhen using /${paramPattern}, can contain placeholders like $1 $2 to use the values of matched groups.`,
    },
    {
        name: paramPattern,
        description: `Regular expression pattern. Parts of the source string matching the pattern will be replaced with the value provided for /${paramString2}.`,
    },
    {
        name: paramRelDest,
        description: `XPath relative to the node(s) indicated by the /${paramXPathSrc} parameter.\n\nWhen specified, qRules will place the results of the string replacement in the indicated location.\n\nCan be used to do string replacement on multiple fields at once.`,
        type: typeXPath,
        relativeTo: paramXPathSrc,
    },
    {
        name: paramAppend,
        description: `(boolean, defaults to false) When true, the result of the string replacement is appended to the current value of the field indicated by the /${paramRelDest} parameter rather than replacing it.\n\nHas no effect if /${paramRelDest} is unspecified.`,
        type: typeBoolean,
    },
];

const requiredParameters = [
    {
        name: paramXPathSrc,
        description: 'XPath to the field containing the value to modify.',
        type: typeXPath,
        dsParentName: paramDsNameSrc,
    },
];

const replaceStringErrorMissingParameter = `Either /${paramString1} or /${paramPattern} must be provided.`;
const errorFailedToSelectSource = 'Failed to select the source node.';
const REPLACE_STRING_ERROR = 'Failed to replace string: ';
const errorFailedToSelectDestination = 'Failed to select the destination node.';
const description = `Returns a string with the instances of /${paramString1} in the source node replaced with /${paramString2}. Can also be used with a regular expression by using the /${paramPattern} parameter.`;

const doReplace = (value, string1, string2, pattern) =>
    value.replaceAll(
        pattern ? new RegExp(pattern, 'g') : string1,
        string2 ?? '',
    );

const handleReplaceStringError = (e) => {
    console.error('Error replacing string', e);

    return failure(REPLACE_STRING_ERROR + e.message);
};

function doSingleReplace(dataSource, xpathSrc, string1, string2, pattern) {
    const sourceNode = getValidNode(
        dataSource,
        xpathSrc,
        errorFailedToSelectSource,
    );

    try {
        const newString = doReplace(
            sourceNode.value(),
            string1,
            string2,
            pattern,
        );

        return success(newString);
    } catch (e) {
        return handleReplaceStringError(e);
    }
}

const doMultiReplace = async (
    dataSource,
    xpathSrc,
    string1,
    string2,
    pattern,
    relDest,
    append,
) => {
    const sourceNodes = getValidNodeSet(
        dataSource,
        xpathSrc,
        errorFailedToSelectSource,
    );
    let replaceCount = 0;

    try {
        for (const node of sourceNodes) {
            const newString = doReplace(
                node.value(),
                string1,
                string2,
                pattern,
            );
            let dest = getValidNode(
                node,
                relDest,
                errorFailedToSelectDestination,
            );

            const fullString = (append ? dest.value() : '') + newString;

            await dest.setValueAsync(fullString);

            replaceCount += 1;
        }

        return success(replaceCount);
    } catch (e) {
        return handleReplaceStringError(e);
    }
};

const executeAsync = ({ dataSources, parameters }) => {
    const dsNameSrc = getParamValue(parameters, paramDsNameSrc);
    const xpathSrc = getParamValue(parameters, paramXPathSrc);
    const string1 = getParamValue(parameters, paramString1);
    const string2 = getParamValue(parameters, paramString2);
    const pattern = getParamValue(parameters, paramPattern);
    const relDest = getParamValue(parameters, paramRelDest);
    const append = getBoolParamValue(parameters, paramAppend);

    if (!(string1 || pattern)) {
        return failure(replaceStringErrorMissingParameter);
    }

    const dataSource = getDataSource(dataSources, dsNameSrc);

    if (relDest) {
        return doMultiReplace(
            dataSource,
            xpathSrc,
            string1,
            string2,
            pattern,
            relDest,
            append,
        );
    }

    return doSingleReplace(dataSource, xpathSrc, string1, string2, pattern);
};

const ReplaceString = {
    description,
    optionalParameters,
    requiredParameters,
    executeAsync,
};

export default ReplaceString;
